import React, { useEffect, useState } from "react";
import {
  Table,
  Form,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

const Convergence = () => {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  /* ================= STATE ================= */
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [abhiyanList, setAbhiyanList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);
  const [villageList, setVillageList] = useState([]);

  const [selectedAbhiyan, setSelectedAbhiyan] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [warning, setWarning] = useState("");
  // const [selectedFilter, setSelectedFilter] = useState("Convergence");

  /* ================= UTILS ================= */
  const safeArray = (res) =>
    Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

  /* ================= AREA HELPERS ================= */
  const areaTitle = selectedVillage.length
    ? "गाव"
    : selectedTaluka.length
    ? "तालुका"
    : selectedDistrict.length
    ? "तालुका"
    : "जिल्हा";

  const areaName = (r) => {
    if (r.village) return r.village;
    if (r.taluka) return r.taluka;
    if (r.district) return r.district;
    return "";
  };

  /* ================= ROUTE CHANGE ================= */
  // const handleFilterChange = (e) => {
  //   const val = e.target.value;
  //   setSelectedFilter(val);
  //   if (val === "Convergence") navigate("/convergence");
  //   if (val === "ConvergenceOne") navigate("/convergenceOne");
  // };

  /* ================= CHECKBOX DROPDOWN ================= */
  const CheckboxDropdown = ({
    label,
    options,
    idKey,
    nameKey,
    selected,
    setSelected,
    disabled,
    dropdownKey,
  }) => {
    const toggle = () => {
      if (!disabled) {
        setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
      }
    };

    return (
      <Form.Group className="mb-3 position-relative">
        <Form.Label>{label}</Form.Label>

        <div
          className={`form-select ${disabled ? "disabled" : ""}`}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={toggle}
        >
          {selected.length === options.length && options.length > 0
            ? "All Selected"
            : selected.length > 0
            ? `${selected.length} Selected`
            : "All"}
        </div>

        {openDropdown === dropdownKey && !disabled && (
          <div
            className="position-absolute bg-white border rounded shadow-sm w-100"
            style={{
              zIndex: 9999,
              maxHeight: 220,
              overflowY: "auto",
              bottom: "100%",
              marginBottom: 8,
            }}
          >
            <div className="px-3 py-2 border-bottom fw-bold">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={options.length > 0 && selected.length === options.length}
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? options.map((o) => o[idKey]) : []
                  )
                }
              />
            </div>

            {options.map((o) => (
              <div key={o[idKey]} className="px-3 py-2">
                <Form.Check
                  type="checkbox"
                  label={o[nameKey]}
                  checked={selected.includes(o[idKey])}
                  onChange={() => {
                    if (
                      dropdownKey === "taluka" &&
                      !selected.includes(o[idKey]) &&
                      selected.length >= 3
                    ) {
                      setWarning("Please select a maximum of 3 Talukas");
                      return;
                    }
                    setWarning("");
                    setSelected(
                      selected.includes(o[idKey])
                        ? selected.filter((x) => x !== o[idKey])
                        : [...selected, o[idKey]]
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    );
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    setLoading(true);

    fetch(`${apiBaseUrl}/convergenceReport/districts`)
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTableData(arr);
        setDistrictList(arr);
        setLoading(false);
      });

    fetch(`${apiBaseUrl}/convergenceReport/abhiyans`)
      .then((r) => r.json())
      .then((json) => setAbhiyanList(safeArray(json)));
  }, [apiBaseUrl]);

  /* ================= ABHIYAN → DISTRICT ================= */
  useEffect(() => {
    if (!selectedAbhiyan.length) return;

    setLoading(true);
    fetch(
      `${apiBaseUrl}/convergenceReport/abhiyans/${selectedAbhiyan.join(
        ","
      )}/districts`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setDistrictList(arr);
        setTableData(arr);

        setSelectedDistrict([]);
        setSelectedTaluka([]);
        setSelectedVillage([]);
        setTalukaList([]);
        setVillageList([]);

        setLoading(false);
      });
  }, [selectedAbhiyan, apiBaseUrl]);

  /* ================= DISTRICT → TALUKA ================= */
  useEffect(() => {
    if (!selectedDistrict.length || !selectedAbhiyan.length) return;

    setLoading(true);
    fetch(
      `${apiBaseUrl}/convergenceReport/abhiyans/${selectedAbhiyan.join(
        ","
      )}/districts/${selectedDistrict.join(",")}/talukas`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setTalukaList(arr);
        setTableData(arr);

        setSelectedTaluka([]);
        setSelectedVillage([]);
        setVillageList([]);

        setLoading(false);
      });
  }, [selectedDistrict, selectedAbhiyan, apiBaseUrl]);

  /* ================= TALUKA → VILLAGE ================= */
  useEffect(() => {
    if (
      !selectedAbhiyan.length ||
      !selectedDistrict.length ||
      !selectedTaluka.length
    )
      return;

    setLoading(true);
    fetch(
      `${apiBaseUrl}/convergenceReport/abhiyans/${selectedAbhiyan.join(
        ","
      )}/districts/${selectedDistrict.join(
        ","
      )}/talukas/${selectedTaluka.join(",")}/villages`
    )
      .then((r) => r.json())
      .then((json) => {
        const arr = safeArray(json);
        setVillageList(arr);
        setTableData(arr);
        setSelectedVillage([]);
        setLoading(false);
      });
  }, [selectedAbhiyan, selectedDistrict, selectedTaluka, apiBaseUrl]);

  /* ================= VILLAGE → STOP DRILLDOWN ================= */
  useEffect(() => {
    if (selectedVillage.length && villageList.length) {
      const filtered = villageList.filter((v) =>
        selectedVillage.includes(v.village_id)
      );
      setTableData(filtered);
    }
  }, [selectedVillage, villageList]);

  /* ================= TOTALS ================= */
  const totals = tableData.reduce(
    (t, r) => {
      t.swach += +r.tbl_swachya_bharat_abhiyan || 0;
      t.ujj += +r.tbl_ujjwala_yojana || 0;
      t.jal += +r.tbl_jaljivan_mission || 0;
      t.saubh += +r.tbl_saubhagya_yojana || 0;
      t.jivo += +r.tbl_gramin_jivannotya || 0;
      t.scst += +r.tbl_dalit_vasti_sudhar || 0;
      t.thakk += +r.tbl_takkar_bappa || 0;
      t.zp += +r.tbl_jilha_parishad || 0;
      t.ps += +r.tbl_panchayat_samiti || 0;
      t.gp += +r.tbl_grampanchayat_samiti || 0;
      t.csr += +r.tbl_crs_nidhi || 0;
      return t;
    },
    {
      swach: 0,
      ujj: 0,
      jal: 0,
      saubh: 0,
      jivo: 0,
      scst: 0,
      thakk: 0,
      zp: 0,
      ps: 0,
      gp: 0,
      csr: 0,
    }
  );

  /* ================= EXPORT EXCEL ================= */
  const exportToExcel = () => {
    const rows = tableData.map((r, i) => ({
      "अ.क्र": i + 1,
      [areaTitle]: areaName(r),
      "स्वच्छ भारत अभियान": r.tbl_swachya_bharat_abhiyan || 0,
      "उज्ज्वला योजना": r.tbl_ujjwala_yojana || 0,
      "जलजीवन मिशन": r.tbl_jaljivan_mission || 0,
      "सौभ्याग्य योजना": r.tbl_saubhagya_yojana || 0,
      "राष्ट्रीय ग्रामीण जीवनोन्नती मिशन": r.tbl_gramin_jivannotya || 0,
      "अनुसूचित जाती व नवबौध्द": r.tbl_dalit_vasti_sudhar || 0,
      "ठक्कर बाप्पा": r.tbl_takkar_bappa || 0,
      "जिल्हा परिषद सेस": r.tbl_jilha_parishad || 0,
      "पंचायत समिती सेस": r.tbl_panchayat_samiti || 0,
      "ग्रामपंचायत सेस": r.tbl_grampanchayat_samiti || 0,
      "CSR निधी": r.tbl_crs_nidhi || 0,
    }));

    rows.push({
      "अ.क्र": "",
      [areaTitle]: "Total",
      "स्वच्छ भारत अभियान": totals.swach,
      "उज्ज्वला योजना": totals.ujj,
      "जलजीवन मिशन": totals.jal,
      "सौभ्याग्य योजना": totals.saubh,
      "राष्ट्रीय ग्रामीण जीवनोन्नती मिशन": totals.jivo,
      "अनुसूचित जाती व नवबौध्द": totals.scst,
      "ठक्कर बाप्पा": totals.thakk,
      "जिल्हा परिषद सेस": totals.zp,
      "पंचायत समिती सेस": totals.ps,
      "ग्रामपंचायत सेस": totals.gp,
      "CSR निधी": totals.csr,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Convergence_Report");
    XLSX.writeFile(wb, "Convergence_Report.xlsx");
  };

  /* ================= UI ================= */
  return (
    <Container fluid>
      <Row>
        <Col md={2} className="p-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/")}
            className="mb-3 w-100"
            style={{ borderRadius: "6px", fontWeight: 500 }}
          >
            Back
          </Button>

          <div className="p-3 shadow-sm" style={{ background: "#EAF0F9" }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 style={{ fontWeight: "bold", color: "#1F3C88" }}>Filters</h5>
              <div
                title="Refresh"
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: "#EAF2FF",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out",
                }}
              >
                <FaSyncAlt size={16} style={{ color: "#1F3C88" }} />
              </div>
            </div>

            <CheckboxDropdown
              label="अभियान"
              options={abhiyanList}
              idKey="abhiyan_id"
              nameKey="abhiyan_name"
              selected={selectedAbhiyan}
              setSelected={setSelectedAbhiyan}
              dropdownKey="abhiyan"
            />

            <CheckboxDropdown
              label="जिल्हा"
              options={districtList}
              idKey="district_id"
              nameKey="district"
              selected={selectedDistrict}
              setSelected={setSelectedDistrict}
              disabled={!districtList.length}
              dropdownKey="district"
            />

            <CheckboxDropdown
              label="तालुका"
              options={talukaList}
              idKey="taluka_id"
              nameKey="taluka"
              selected={selectedTaluka}
              setSelected={setSelectedTaluka}
              disabled={!talukaList.length}
              dropdownKey="taluka"
            />

            <CheckboxDropdown
              label="गाव"
              options={villageList}
              idKey="village_id"
              nameKey="village"
              selected={selectedVillage}
              setSelected={setSelectedVillage}
              disabled={!villageList.length}
              dropdownKey="village"
            />
          </div>
        </Col>

        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
            <h5 className="fw-bold mb-0" style={{ color: "#1F3C88" }}>
              विविध योजनांच्या कृतीसंगमातून लाभ दिलेल्या लाभार्थ्यांची संख्या
            </h5>

            <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
              <Form.Group controlId="searchBox" style={{ width: "250px" }}>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: "6px",
                    height: "38px",
                    fontSize: "14px",
                    border: "1px solid #ced4da",
                  }}
                />
              </Form.Group>

              <Button
                style={{
                  backgroundColor: "#4da6ff",
                  borderColor: "#4da6ff",
                }}
                onClick={exportToExcel}
              >
                Export to Excel
              </Button>
            </div>
          </div>

          {loading ? (
  <Spinner className="d-block mx-auto mt-5" />
) : (
  <div
    style={{
      maxHeight: "520px",   // vertical scroll height
      overflowY: "auto",
      border: "1px solid #dee2e6",
    }}
  >
    <Table bordered hover responsive className="mt-3 mb-0">
                <thead>
                  <tr>
                    <th>अ.क्र</th>
                    <th>{areaTitle}</th>
                    <th>स्वच्छ भारत अभियान</th>
                    <th>उज्ज्वला योजना</th>
                    <th>जलजीवन मिशन</th>
                    <th>सौभ्याग्य योजना</th>
                    <th>राष्ट्रीय ग्रामीण जीवनोन्नती मिशन</th>
                    <th>अनुसूचित जाती व नवबौध्द</th>
                    <th>ठक्कर बाप्पा</th>
                    <th>जिल्हा परिषद सेस</th>
                    <th>पंचायत समिती सेस</th>
                    <th>ग्रामपंचायत सेस</th>
                    <th>CSR निधी</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="fw-bold bg-light">
                    <td colSpan={2}>Total</td>
                    <td>{totals.swach}</td>
                    <td>{totals.ujj}</td>
                    <td>{totals.jal}</td>
                    <td>{totals.saubh}</td>
                    <td>{totals.jivo}</td>
                    <td>{totals.scst}</td>
                    <td>{totals.thakk}</td>
                    <td>{totals.zp}</td>
                    <td>{totals.ps}</td>
                    <td>{totals.gp}</td>
                    <td>{totals.csr}</td>
                  </tr>

                  {tableData.map((r, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{areaName(r)}</td>
                      <td>{r.tbl_swachya_bharat_abhiyan || 0}</td>
                      <td>{r.tbl_ujjwala_yojana || 0}</td>
                      <td>{r.tbl_jaljivan_mission || 0}</td>
                      <td>{r.tbl_saubhagya_yojana || 0}</td>
                      <td>{r.tbl_gramin_jivannotya || 0}</td>
                      <td>{r.tbl_dalit_vasti_sudhar || 0}</td>
                      <td>{r.tbl_takkar_bappa || 0}</td>
                      <td>{r.tbl_jilha_parishad || 0}</td>
                      <td>{r.tbl_panchayat_samiti || 0}</td>
                      <td>{r.tbl_grampanchayat_samiti || 0}</td>
                      <td>{r.tbl_crs_nidhi || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
</div>
)}

            
         
        </Col>
      </Row>

      {warning && (
        <Alert variant="warning" className="mt-3">
          {warning}
        </Alert>
      )}
    </Container>
  );
};

export default Convergence;
